import React from "react";
import { format } from "date-fns";
import { Alert, Text } from "@networkraildigitalfactory/components";
import MultiHeadingAlert from "../MultiHeadingAlert/MultiHeadingAlert";
import RatingBox from "./RatingBox";
import styles from "./ViewReviewPrep.module.css";
import { createErrorNotice } from "../CreateReviewPrep/ReviewFormHelpers";

const ViewPrepHeading = (props) => {
  const { currentPrep, multiEOYAlert, isPrepCompleted, status, reviewTypeLowerCase } = props;
  const interimRating = currentPrep?.interim?.interimRating;
  const reviewTypeRating = currentPrep?.[reviewTypeLowerCase]?.[`${reviewTypeLowerCase}Rating`];
  const reviewTypeFeedback = currentPrep?.[reviewTypeLowerCase]?.[`${reviewTypeLowerCase}Feedback`];

  // eslint-disable-next-line consistent-return
  const getParams = () => {
    const noRatingMsg = "No rating given for this review";
    // EOY view
    // EOY review submitted prep
    // Interim review completed
    if (
      status === "Submitted" &&
      reviewTypeLowerCase === "eoy" &&
      currentPrep?.interim?.isInterimCompleted
    ) {
      return {
        alertHeading: "Line Manager’s interim comment",
        alertMessage: currentPrep?.interim?.interimFeedback,
        ratingHeading: interimRating ? `Currently tracking at: ${interimRating}` : noRatingMsg,
      };
    }

    // EOY view
    // EOY review completed
    // Interim review incomplete
    if (
      status === "Completed" &&
      reviewTypeLowerCase === "eoy" &&
      !currentPrep?.interim?.isInterimCompleted
    ) {
      return {
        alertHeading: "Line Manager’s EOY comment",
        alertMessage: reviewTypeFeedback,
        ratingHeading: reviewTypeRating ? `Suggested EOY Rating: ${reviewTypeRating}` : noRatingMsg,
        ratingMessage: reviewTypeRating
          ? "Please note: this rating is subject to the usual calibration process and may change."
          : null,
      };
    }

    // Interim view
    // Interim review completed
    if (reviewTypeLowerCase === "interim" && status === "Completed") {
      return {
        alertHeading: "Line manager’s comment",
        alertMessage: reviewTypeFeedback,
        ratingHeading: reviewTypeRating
          ? `Currently tracking at: ${reviewTypeRating}`
          : noRatingMsg,
      };
    }

    // EOY view
    // Interim review completed
    // EOY prep not submitted
    if (
      currentPrep?.interim?.isInterimCompleted &&
      reviewTypeLowerCase === "eoy" &&
      !currentPrep?.eoy?.isEoyCompleted
    ) {
      return {
        alertHeading: "Line Manager’s interim comment",
        alertMessage: currentPrep?.interim?.interimFeedback,
      };
    }

    // EOY view
    // Interim review completed
    // EOY review completed
    if (
      reviewTypeLowerCase === "eoy" &&
      status === "Completed" &&
      currentPrep?.interim?.isInterimCompleted
    ) {
      return {
        ratingHeading: reviewTypeRating ? `Suggested EOY Rating: ${reviewTypeRating}` : noRatingMsg,
        ratingMessage: reviewTypeRating
          ? "Please note: this rating is subject to the usual calibration process and may change."
          : null,
        isMultiAlert: true,
        multiAlertContent: multiEOYAlert,
      };
    }

    // Interim view
    // No interim completed
    return {
      alertHeading: null,
      alertMessage: null,
      ratingMessage: null,
      ratingHeading: null,
    };
  };

  const getErrorNotices = () => {
    let interimErrorNotice;
    let eoyErrorNotice;
    if (reviewTypeLowerCase === "eoy") {
      if (currentPrep?.interim?.isInterimCompleted && !currentPrep?.interim?.interimPrep) {
        interimErrorNotice = "No interim prep submitted";
      }

      if (currentPrep?.interim?.status === "submitted") {
        interimErrorNotice = "No interim review completed";
      }

      if (
        !currentPrep?.interim ||
        (!currentPrep?.interim?.isInterimCompleted && !currentPrep?.interim?.interimPrep)
      ) {
        interimErrorNotice = "No interim prep or review completed";
      }

      if (!currentPrep?.eoy?.eoyPrep?.isEoyPrepCompleted || !currentPrep?.eoy?.eoyPrep) {
        eoyErrorNotice = "No EOY prep submitted";
      }
    } else if (reviewTypeLowerCase === "interim") {
      if (currentPrep?.interim?.isInterimCompleted && !currentPrep?.interim?.interimPrep) {
        interimErrorNotice = "No interim prep submitted";
      }
    }
    return { interimErrorNotice, eoyErrorNotice };
  };

  const {
    alertHeading,
    alertMessage,
    ratingHeading,
    ratingMessage,
    isMultiAlert,
    multiAlertContent,
  } = getParams();

  const { interimErrorNotice, eoyErrorNotice } = getErrorNotices();

  return (
    <>
      {isPrepCompleted && (
        <Text wrapper="span" type="body">
          Created on {format(new Date(currentPrep.createdAt), "dd/MM/yyyy ")}
        </Text>
      )}
      {interimErrorNotice && (
        <div className={styles.noInterimComplete_row}>{createErrorNotice(interimErrorNotice)}</div>
      )}
      {eoyErrorNotice && (
        <div className={styles.noEOYCompleted_row}>{createErrorNotice(eoyErrorNotice)}</div>
      )}
      {isMultiAlert ? (
        <MultiHeadingAlert content={multiAlertContent} />
      ) : (
        alertHeading && (
          <Alert
            color="blue"
            title={alertHeading}
            message={alertMessage}
            style={{ margin: "24px 0" }}
            fontSize="md"
          />
        )
      )}
      {ratingHeading && <RatingBox heading={ratingHeading} message={ratingMessage || null} />}
    </>
  );
};

export default ViewPrepHeading;
